//components
import {
  SectionWrap,
  Kicker,
  KickerDot,
  SectionTitle,
  SectionLede,
  Grad,
} from '@/containers-ai/SectionHeading';
import { PillLink } from '@/containers-ai/Buttons';
import { CountUp } from '@/containers-ai/CountUp';

//styles
import {
  LiveSection,
  BigProof,
  BigProofValue,
  BigProofText,
  ExceptionsBanner,
  ExceptionsBannerText,
  TaskGrid,
  TaskCard,
  TaskTitle,
  TaskDescription,
  TaskMetricWrap,
  TaskMetric,
  TaskMetricLabel,
  CustomCard,
  CustomCardKicker,
  CustomCardTitle,
  CustomCardCopy,
  PlusDivider,
  SummaryRow,
  SummaryTitle,
  SummaryPanel,
  SummaryValue,
  SummaryCopy,
} from './LiveInProduction.styles';

const tasks = [
  {
    title: 'Document collection & filing',
    description:
      'Quotes, tenders, appointments, settlement — executed live inside your operation, under the rules you set.',
    metric: 'Needs metric',
  },
  {
    title: 'Tracking & status updates',
    description:
      'We get status from carriers so your team stops making check calls, and push clean updates to the people who need them.',
    metric: 'Needs metric',
  },
  {
    title: 'Tender failures',
    description:
      'When a tender falls through, we re-tender down your routing guide and re-book, before the miss ever reaches your team.',
    metric: '8–10%',
  },
  {
    title: 'Proactive load audits',
    description:
      'We check every load before pickup and flag at-risk transit early, so problems surface before they turn into penalties.',
    metric: 'Needs metric',
  },
  {
    title: 'Claims processing',
    description:
      'We gather the documentation, validate the claim, and draft the response for your approval, so claims actually get filed and recovered.',
    metric: 'Needs metric',
  },
  {
    title: 'Scheduling & re-scheduling',
    description:
      'We book dock appointments against carrier and facility availability, and when a pickup slips, we re-schedule and notify everyone who needs to know.',
    metric: 'Needs metric',
  },
];

export function LiveInProduction() {
  return (
    <LiveSection id="ai">
      <SectionWrap>
        <Kicker>
          <KickerDot />
          What we automate
        </Kicker>
        <SectionTitle style={{ maxWidth: '32ch' }}>
          The repetitive work that eats your team&rsquo;s day. <br />
          <Grad>We take it off your plate.</Grad>
        </SectionTitle>
        <SectionLede>
          Every shipper runs the same manual tasks over and over. Loadsmart AI
          takes the whole task, not a step of it &mdash; and our Freight Experts
          step in whenever something needs a human.
        </SectionLede>

        <BigProof>
          <BigProofValue>
            <CountUp value={3000} />+
          </BigProofValue>
          <BigProofText>
            real TMS objects <b>Copilot AI has orchestrated in production,</b>
            <br />
            shipments, appointments, charges, invoices, target rates.
          </BigProofText>
        </BigProof>

        <ExceptionsBanner>
          <ExceptionsBannerText>
            Loadsmart Freight Experts resolve the exceptions
          </ExceptionsBannerText>
        </ExceptionsBanner>

        <TaskGrid>
          {tasks.map((task) => (
            <TaskCard key={task.title}>
              <TaskTitle>{task.title}</TaskTitle>
              <TaskDescription>{task.description}</TaskDescription>
              <TaskMetricWrap>
                <TaskMetric>{task.metric}</TaskMetric>
                <TaskMetricLabel>
                  overpayment on flat rates eliminated
                </TaskMetricLabel>
              </TaskMetricWrap>
            </TaskCard>
          ))}
        </TaskGrid>

        <CustomCard>
          <div>
            <CustomCardKicker>Custom &mdash; anything else</CustomCardKicker>
            <CustomCardTitle>
              If you can describe the task, we&rsquo;ll build the agent to run
              it.
            </CustomCardTitle>
            <CustomCardCopy>
              Bring the repetitive, rules-based work that eats your team&rsquo;s
              day. We build it, we run it, and our Freight Experts own the
              exceptions.
            </CustomCardCopy>
          </div>
          <PillLink href="/contact/#contact-form">
            Build Your workflow &#8594;
          </PillLink>
        </CustomCard>

        <PlusDivider aria-hidden="true">+</PlusDivider>

        <SummaryRow>
          <SummaryTitle>
            The more Loadsmart AI runs your freight,{' '}
            <span>the more it resolves on its own.</span>
          </SummaryTitle>
          <SummaryPanel>
            <SummaryValue>85% to 95%+</SummaryValue>
            <SummaryCopy>
              Loadsmart AI senses, decides and acts on every load, then writes
              the outcome back into your system of record.{' '}
              <b>
                It compounds on your own outcomes so it resolves more on its own
                over time.
              </b>
            </SummaryCopy>
          </SummaryPanel>
        </SummaryRow>
      </SectionWrap>
    </LiveSection>
  );
}
